import type { CSSProperties } from 'react';
import type { FormatSettings } from '../../../lib/format';
import {
  display,
  font,
  INK,
  LINE,
  MUTED,
  ShopMark,
  type ShopHeader,
} from '../../sales/print/documents';
import { formatCell, type ReportMeta, type ReportTable } from '../lib/reportTable';
import { poweredByText } from '../../../components/PoweredBy';

/**
 * The printed / PDF form of a report: shop letterhead, report title and period, then the full
 * table with exact figures (no charts or KPI tiles). Header rows repeat on every page.
 */
export function ReportDocument({
  shop,
  meta,
  table,
  format,
}: {
  shop: ShopHeader;
  meta: ReportMeta;
  table: ReportTable;
  format: FormatSettings;
}) {
  const accent = shop.accent ?? '#1565C0';
  const wide = table.columns.length > 6;
  const cellPad = wide ? '5pt 5pt' : '6pt 6pt';
  const th: CSSProperties = {
    fontSize: '7.5pt',
    fontWeight: 650,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: MUTED,
    padding: cellPad,
    borderBottom: `1.2pt solid ${INK}`,
    verticalAlign: 'bottom',
  };
  const contact = [shop.phone, shop.email].filter(Boolean).join('  ·  ');
  return (
    <div
      data-testid="report-document"
      style={{
        fontFamily: font,
        color: INK,
        fontSize: wide ? '8.5pt' : '9.5pt',
        lineHeight: 1.4,
        background: '#fff',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16pt',
          alignItems: 'flex-start',
          paddingBottom: '10pt',
          borderBottom: `2pt solid ${accent}`,
        }}
      >
        <div style={{ display: 'flex', gap: '10pt', alignItems: 'center' }}>
          <ShopMark shop={shop} accent={accent} size={38} />
          <div>
            <div style={{ fontFamily: display, fontWeight: 700, fontSize: '13pt' }}>
              {shop.name}
            </div>
            {shop.address && <div style={{ color: MUTED }}>{shop.address}</div>}
            {contact && <div style={{ color: MUTED }}>{contact}</div>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: display, fontWeight: 700, fontSize: '16pt' }}>{meta.title}</div>
          {meta.period && <div style={{ fontWeight: 600 }}>{meta.period}</div>}
          <div style={{ color: MUTED, fontSize: '8pt' }}>Generated {meta.generatedAt}</div>
        </div>
      </div>

      {table.rows.length === 0 ? (
        <p style={{ color: MUTED, marginTop: '16pt' }}>No entries for this period.</p>
      ) : (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '12pt',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <thead style={{ display: 'table-header-group' }}>
            <tr>
              {table.columns.map((c, ci) => (
                <th key={ci} style={{ ...th, textAlign: c.kind === 'text' ? 'left' : 'right' }}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((r, ri) => {
              const total = table.totalRows.has(ri);
              return (
                <tr key={ri} style={{ breakInside: 'avoid' }}>
                  {table.columns.map((c, ci) => (
                    <td
                      key={ci}
                      style={{
                        padding: cellPad,
                        textAlign: c.kind === 'text' ? 'left' : 'right',
                        whiteSpace: c.kind === 'text' ? 'normal' : 'nowrap',
                        verticalAlign: 'top',
                        fontWeight: total ? 700 : undefined,
                        borderTop: total ? `1pt solid ${INK}` : undefined,
                        borderBottom: total ? `2.5pt double ${INK}` : `0.6pt solid ${LINE}`,
                        background: total ? '#F9FAFB' : undefined,
                      }}
                    >
                      {formatCell(r[ci] ?? null, c, format)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div
        style={{
          marginTop: '14pt',
          paddingTop: '6pt',
          borderTop: `0.6pt solid ${LINE}`,
          color: MUTED,
          fontSize: '7.5pt',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>
          {table.rows.length - table.totalRows.size} row
          {table.rows.length - table.totalRows.size === 1 ? '' : 's'} · amounts in{' '}
          {format.currencyCode}
        </span>
        <span>{shop.name}</span>
      </div>
      <div style={{ marginTop: '4pt', textAlign: 'center', color: MUTED, fontSize: '7pt' }}>
        {poweredByText()}
      </div>
    </div>
  );
}
